import { createConsumer } from "@rails/actioncable"
import type {
  Alert,
  DeviceSettings,
  Entry as UserEntry,
  EntryTemplate,
  Event as UserEvent,
  Follow,
  FollowInvite,
  Phone,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  useMemo,
  useEffect,
} from "react"

import type { DeletedAtItem } from "./DeletedAtItem"
import { useDeviceSettingsHandler } from "./useDeviceSettingsHandler"
import { useEntryTemplatesHandler } from "./useEntryTemplatesHandler"
import { useFollowHandler } from "./useFollowHandler"
import { usePhoneHandler } from "./usePhoneHandler"
import { useUserEntriesHandler } from "./useUserEntriesHandler"
import { useUserEventsHandler } from "./useUserEventsHandler"
import {
  useAuthContext,
  usePageLifecycleContext,
} from "src/contexts"
import {
  useEventReloading,
  useEntryReloading,
} from "src/contexts/LoadingContexts"
import { useIsLeader } from "src/hooks/utils"
import { PageLifecycle } from "src/types"
import { noticeError } from "src/utils"

interface WebSocketData {
  message: {
    alert?: Alert; // do nothing for now
    device_settings?: DeviceSettings;
    entry?: UserEntry | DeletedAtItem<UserEntry>;
    entry_template?: EntryTemplate | DeletedAtItem<EntryTemplate>;
    event?: UserEvent | DeletedAtItem<UserEvent>;
    follow?: Follow | DeletedAtItem<Follow>;
    follow_invite?: FollowInvite; // do nothing for now
    phone?: Phone | DeletedAtItem<Phone>;
    load_new_data?: {
      model: "events" | "entries";
      follow_id: Follow["id"];
    };
  };
}

interface Props {
  baseURL: string;
}

function getWebSocketUrl(token: string, baseURL: string) {
  const url = new URL(baseURL)
  const protocol = url.protocol === "http:" ? "ws:" : "wss:"
  const domain = url.hostname
  return `${protocol}//${domain}/cable?auth_token=${token}`
}

export function WebSocketHandler(props: Props) {
  const { baseURL } = props
  const pageLifecycle = usePageLifecycleContext()
  const {
    authCookie,
    isExpired,
  } = useAuthContext()

  const entryTemplatesHandler = useEntryTemplatesHandler()
  const userEventsHandler = useUserEventsHandler()
  const userEntriesHandler = useUserEntriesHandler()
  const phoneHandler = usePhoneHandler()
  const followHandler = useFollowHandler()
  const deviceSettingsHandler = useDeviceSettingsHandler()

  const { requestReload: requestReloadEvents } = useEventReloading()
  const { requestReload: requestReloadEntries } = useEntryReloading()

  const cable = useMemo(
    () => authCookie?.access_token && !isExpired && createConsumer(getWebSocketUrl(
      authCookie.access_token,
      baseURL,
    )),
    [
      authCookie,
      isExpired,
      baseURL,
    ],
  )

  // only one tab can subscribe to websockets at a time
  const isWebsocketLeader = useIsLeader("sugarmate-websocket-handler")

  const shouldConnect = (
    isWebsocketLeader &&
    authCookie?.access_token &&
    !isExpired && (
      pageLifecycle === PageLifecycle.Active ||
      pageLifecycle === PageLifecycle.Hidden ||
      pageLifecycle === PageLifecycle.Passive
    )
  )

  // listen for new user events and user entries and add them to the collection
  useEffect(
    () => {
      if (!shouldConnect || !cable) {
        return
      }

      const subscription = cable.subscriptions.create(
        { channel: "LiveChannel" },
        {
          // TODO handle connections / disconnections
          // https://guides.rubyonrails.org/action_cable_overview.html
          connected: () => {
            // console.debug("action cable connected")
          },
          disconnected: () => {
            // console.debug("action cable disconnected")
          },
          rejected: () => {
            noticeError(
              "action cable rejected",
              "WebsocketRejected",
            )
          },
          received: (data: WebSocketData) => {
            const { message } = data

            if (!message) {
              return
            }

            if (message.load_new_data) {
              const {
                model: sugarmateModel,
                follow_id,
              } = message.load_new_data

              if (sugarmateModel === "events") {
                requestReloadEvents(follow_id)
              } else if (sugarmateModel === "entries") {
                requestReloadEntries(follow_id)
              }
            }

            if (message.alert) {
              // do nothing for now
            }

            if (message.event) {
              void userEventsHandler(message.event)
            }

            if (message.entry_template) {
              void entryTemplatesHandler(message.entry_template)
            }

            if (message.entry) {
              void userEntriesHandler(message.entry)
            }

            if (message.follow) {
              void followHandler(message.follow)
            }

            if (message.follow_invite) {
              // do nothing for now
            }

            if (message.phone) {
              void followHandler(message.phone)
            }

            if (message.device_settings) {
              void followHandler(message.device_settings)
            }
          },
        },
      )

      return () => {
        subscription.unsubscribe()
      }
    },
    [
      cable,
      userEventsHandler,
      userEntriesHandler,
      entryTemplatesHandler,
      phoneHandler,
      deviceSettingsHandler,
      followHandler,
      shouldConnect,
      requestReloadEvents,
      requestReloadEntries,
    ],
  )

  return null
}
